import { keyBy } from 'lodash';
import i18next from 'i18next';

import utilsXLS from '@commons/utils/makeXLS';

import { INFO_SHEET_STORE_COLUMN } from '@admin/products/recipes/utils/exportRecipesByStoreColumns';

import { buildProductRecipeRow, prepareProductForExport } from './productExportRows';
import { getColumnsSettings } from '../components/ProductExportModal';

/**
 * Build the column settings for the export-by-store sheet: the standard product columns with the
 * variant column, plus an "Etablissement" column in C. Brand / product type / cashier SKU columns
 * are added the same way as the standard export when applicable.
 */
const getStoreColumnsSettings = (hasMultipleBrands, hasSimpleModifierManagement, hasCashierSku) => {
  const columns = getColumnsSettings(true);

  if (hasMultipleBrands) {
    columns.splice(2, 0, {
      propertyKey: 'product.brandName',
      name: i18next.t('GENERAL.BRAND'),
    });
  }

  if (hasSimpleModifierManagement) {
    columns.splice(2, 0, {
      propertyKey: 'product.type',
      name: i18next.t('ADMIN.PRODUCTS.PRODUCT_TYPE_EXPORT_COLUMN_TITLE'),
    });
  }

  // Inserted last so it lands in column C (right after id and name) in the base case
  columns.splice(2, 0, {
    propertyKey: 'recipe.storeName',
    name: i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE_STORE_COLUMN'),
  });

  if (hasCashierSku) {
    columns.push({
      propertyKey: 'product.cashierProductSku',
      name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_SYNC_CASHIER_PRODUCT_SKU'),
    });
  }

  return columns;
};

/**
 * Generate the "export by store" products XLS. Each product recipe is expanded per selected store
 * to the variant that store uses (cost resolved per store, nested variants included). A recipe with
 * no variant configured on any selected store is emitted as a single row with "all" stores. A second
 * "Informations" sheet lists the selected stores.
 *
 * @param {Object} params
 * @param {Object[]} params.rawProductDataList - Raw { product, recipes } export data
 * @param {Object[]} params.clientChannels
 * @param {string} params.defaultChannelId
 * @param {Object[]} params.clientBrands
 * @param {boolean} params.hasMultipleBrands
 * @param {boolean} params.hasSimpleModifierManagement
 * @param {Object} params.currency
 * @param {{ id: string, name: string }[]} params.selectedStores
 * @param {{ compositions: Object[], storeResolutions: Object[] }} params.storeVariantData
 */
export const exportProductsByStore = ({
  rawProductDataList,
  clientChannels,
  defaultChannelId,
  clientBrands,
  hasMultipleBrands,
  hasSimpleModifierManagement,
  currency,
  selectedStores,
  storeVariantData,
}) => {
  if (!selectedStores.length) {
    return;
  }

  const { compositions, storeResolutions } = storeVariantData;

  const compositionByKey = keyBy(compositions, 'combinationKey');

  // storeId -> (recipeId -> { combinationKey, topVariantName })
  const resolutionByStore = new Map();

  storeResolutions.forEach(({ storeId, recipeId, combinationKey, topVariantName }) => {
    if (!resolutionByStore.has(storeId)) {
      resolutionByStore.set(storeId, new Map());
    }

    resolutionByStore.get(storeId).set(recipeId, { combinationKey, topVariantName });
  });

  // Recipes mapped to a variant on at least one selected store; the others are identical everywhere
  const recipesWithVariants = new Set(storeResolutions.map(({ recipeId }) => recipeId));

  const mainVariantName = i18next.t('ADMIN.RECIPES.MAIN_RECIPE_TAB');
  const allStoresLabel = i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE_ALL_STORES');

  const rows = [];
  let hasCashierSku = false;

  rawProductDataList.forEach(({ product, recipes }) => {
    prepareProductForExport(product, clientBrands, defaultChannelId);

    if (product.cashierProductSku) {
      hasCashierSku = true;
    }

    if (!recipes.length) {
      rows.push({
        product,
        recipe: {
          channel: i18next.t('ADMIN.RECIPES.EXPORT_VALUE_NO_ENTITY'),
          variantName: mainVariantName,
          storeName: allStoresLabel,
        },
      });

      return;
    }

    recipes.forEach((recipe) => {
      const isIngredient = recipe.isIngredient;
      const channel = clientChannels.find(({ id }) => id === recipe.channelId) || {};
      const channelId = channel.id || defaultChannelId;
      const mainRawCost = isIngredient ? recipe.cost : recipe.cost[channelId];

      // Ingredient products and recipes without any store variant are identical everywhere → one row
      if (isIngredient || !recipesWithVariants.has(recipe.id)) {
        rows.push(
          buildProductRecipeRow(product, recipe, channel, channelId, mainRawCost, {
            variantName: mainVariantName,
            storeName: allStoresLabel,
          }),
        );

        return;
      }

      selectedStores.forEach((store) => {
        const resolution = resolutionByStore.get(store.id)?.get(recipe.id);
        const resolvedCost = resolution
          ? compositionByKey[resolution.combinationKey]?.costByChannelId?.[channelId]
          : null;

        rows.push(
          buildProductRecipeRow(product, recipe, channel, channelId, resolvedCost ?? mainRawCost, {
            variantName: resolution?.topVariantName ?? mainVariantName,
            storeName: store.name,
          }),
        );
      });
    });
  });

  const productSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.PRODUCT_PLURAL'),
    getStoreColumnsSettings(hasMultipleBrands, hasSimpleModifierManagement, hasCashierSku),
    rows,
    currency,
  );

  const infoSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.INFORMATIONS'),
    [INFO_SHEET_STORE_COLUMN()],
    selectedStores.map((store) => ({ storeName: store.name })),
    currency,
  );

  utilsXLS.makeXLS(i18next.t('ADMIN.PRODUCTS.EXPORT_BY_STORE_FILENAME'), [productSheet, infoSheet]);
};
